/**
 * <s-media-carousel> — one-slide-at-a-time carousel.
 *
 * Buttons use commandfor/command (--prev, --next) to move the track by one full slide.
 * Native scroll-snap handles touch swipes; the status line shows the position.
 * Optional: data-autoplay="ms" advances automatically (paused on hover/focus and when the
 * tab is hidden, disabled for prefers-reduced-motion); data-loop wraps from last to first.
 *
 * Markup:
 *   [data-carousel-track]  — horizontal scroll-snap container with one slide per child.
 *   [data-carousel-prev] / [data-carousel-next] — optional command buttons (disabled at the ends).
 *   [data-carousel-status] — optional text; data-status-template holds "[current]" and "[total]".
 */
class SMediaCarousel extends HTMLElement {
  #ac;
  #raf = 0;
  #timer = 0;
  #paused = false;
  #holdUntil = 0;
  #allSlides = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('command', this.#handleCommand, opts);
    this.#track()?.addEventListener('scroll', this.#scheduleUpdate, { ...opts, passive: true });

    if (this.dataset.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const pause = () => { this.#paused = true; };
      const resume = () => { this.#paused = false; };
      this.addEventListener('pointerenter', pause, opts);
      this.addEventListener('pointerleave', resume, opts);
      this.addEventListener('focusin', pause, opts);
      this.addEventListener('focusout', resume, opts);
      const interval = Math.max(1000, Number(this.dataset.autoplay) || 5000);
      this.#timer = window.setInterval(() => {
        if (!this.#paused && !document.hidden && Date.now() > this.#holdUntil) this.#go(1);
      }, interval);
    }
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
    clearInterval(this.#timer);
  }

  #track() {
    return this.querySelector('[data-carousel-track]');
  }

  /** Width of one slide plus the track gap, so multi-card tracks move one card at a time. */
  #slideSize(track) {
    const first = track.children[0];
    if (!first) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap || track.clientWidth;
  }

  #index() {
    const track = this.#track();
    if (!track || !track.clientWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / this.#slideSize(track));
  }

  #go(step) {
    const track = this.#track();
    if (!track) return;
    const total = track.children.length;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const size = this.#slideSize(track);
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atEnd = Math.abs(track.scrollLeft) >= maxScroll - 2;
    let target = this.#index() + step;
    if (this.hasAttribute('data-loop')) {
      if (step > 0 && atEnd) target = 0;
      else target = (target + total) % total;
    }
    target = Math.min(Math.max(target, 0), total - 1);
    const left = Math.min(target * size, maxScroll) * (rtl ? -1 : 1);
    track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
  }

  /**
   * Scrolls to the slide whose data-media-id matches (used when a shopper picks an offer, so the
   * gallery shows that offer's photo). Autoplay holds off for a while so it doesn't move away.
   * Returns true when a matching slide exists.
   */
  showMedia(mediaId) {
    const track = this.#track();
    if (!track || !mediaId) return false;
    const slides = [...track.children];
    const target = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (target < 0) return false;
    this.#holdUntil = Date.now() + 10000;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const left = Math.min(target * this.#slideSize(track), maxScroll) * (rtl ? -1 : 1);
    track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    return true;
  }

  /**
   * Shows only one offer's photos, in the order of `ids` (product media IDs). Falls back to every
   * slide when none of the IDs is in the gallery. Slides are moved, never re-created, so zoom
   * buttons keep working.
   */
  showOffer(ids = []) {
    const track = this.#track();
    if (!track) return;
    if (!this.#allSlides) this.#allSlides = [...track.children];
    const byId = new Map(this.#allSlides.map((slide) => [slide.dataset.mediaId, slide]));
    const chosen = ids.map((id) => byId.get(id)).filter(Boolean);
    const next = chosen.length ? chosen : this.#allSlides;
    // Slides can arrive hidden from the server (outside the starting offer's set); reveal the ones shown now.
    next.forEach((slide) => { slide.hidden = false; });
    const same = next.length === track.children.length && next.every((slide, i) => track.children[i] === slide);
    if (!same) track.replaceChildren(...next);
    this.#holdUntil = Date.now() + 10000;
    track.scrollTo({ left: 0, behavior: 'auto' });
    this.#update();
  }

  #handleCommand = (event) => {
    if (event.command === '--next') this.#go(1);
    else if (event.command === '--prev') this.#go(-1);
  };

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#track();
    if (!track) return;
    const total = track.children.length;
    const index = this.#index();
    const loop = this.hasAttribute('data-loop');
    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (prev) prev.disabled = !loop && index <= 0;
    if (next) next.disabled = !loop && index >= total - 1;
    const status = this.querySelector('[data-carousel-status]');
    const template = this.dataset.statusTemplate;
    if (status && template) {
      const text = template.replace('[current]', String(index + 1)).replace('[total]', String(total));
      if (status.textContent !== text) status.textContent = text;
    }
  }
}

if (!customElements.get('s-media-carousel')) customElements.define('s-media-carousel', SMediaCarousel);
